import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Upload } from 'lucide-react';
import type { ColDef, ValueFormatterParams } from 'ag-grid-community';
import { useAuth } from '../../contexts/AuthContext';
import { canEdit, canView } from '../../utils/permissions';
import { LopAgGrid } from '../../components/LopAgGrid';
import {
  uploadBankFiles,
  getBankImports,
  getBankImportExceptions,
  type BankFileUploadResult,
  type BankImportDef,
  type BankImportException,
} from '../../api/bankFiles';
import { t } from '../../i18n/t';
import './BankFilesPage.css';
import { showErrorToast, describeError } from '../../components/ErrorToast';
import { LopButton } from '../../components/LopButton';
import { LopFilePicker } from '../../components/LopFilePicker';
import { formatDateOnly, formatDateTimeIL } from '../../utils/dateTime';
import { LopDateInput } from '../../components/LopDateInput';

// ─── Import registry ────────────────────────────────────────────────────────────
// Sector cards are driven by the BankImportDefs registry (GET /api/bank-imports),
// not a hardcoded list — add/rename imports in the DB, no code change here.
interface BankDescriptor {
  importId: number;
  bankId: number;
  name: string;
  sourceKind: string; // 'folder' | 'file' — controls the picker mode
  lastImportDate?: string | null;
}

// ─── Types ────────────────────────────────────────────────────────────────────
interface UploadState {
  loading: boolean;
  result: BankFileUploadResult | null;
  error: string | null;
  /** Wall-clock of the finished run, so the summary can report how long it took. */
  durationMs: number | null;
}

/** "1:23" past a minute, "42" below it — an ingestion can legitimately run tens of seconds. */
function formatElapsed(ms: number): string {
  const total = Math.floor(ms / 1000);
  if (total < 60) return String(total);
  const mins = Math.floor(total / 60);
  const secs = total % 60;
  return `${mins}:${String(secs).padStart(2, '0')}`;
}

// ─── Bank Sector Card ─────────────────────────────────────────────────────────
interface BankSectorProps {
  bank: BankDescriptor;
  canUpload: boolean;
  onUploadSuccess: (bankId: number, landedValueDate?: string) => void;
}

function BankSector({ bank, canUpload, onUploadSuccess }: BankSectorProps) {
  const isFolder = bank.sourceKind === 'folder';
  const [files, setFiles] = useState<File[]>([]);
  const [valueDate, setValueDate] = useState<string>('');
  const [state, setState] = useState<UploadState>({
    loading: false,
    result: null,
    error: null,
    durationMs: null,
  });

  // A real ingestion can run for tens of seconds (Poalim shreds ~50 MB of files), and a
  // silent frozen button reads as a hung screen. Tick a live elapsed counter while the
  // request is in flight so the user can see it IS working.
  const [elapsedMs, setElapsedMs] = useState(0);
  useEffect(() => {
    if (!state.loading) return;
    const startedAt = Date.now();
    setElapsedMs(0);
    const timer = window.setInterval(() => setElapsedMs(Date.now() - startedAt), 200);
    return () => window.clearInterval(timer);
  }, [state.loading]);

  const handleFilesChange = useCallback(
    (selected: File[]) => {
      setFiles(selected);
      // Clear previous result when the user picks new files
      setState({ loading: false, result: null, error: null, durationMs: null });
    },
    [],
  );

  const handleUpload = useCallback(async () => {
    if (files.length === 0) return;
    const startedAt = Date.now();
    // Zero the counter in the SAME render that flips loading on. The effect below also
    // resets it, but it runs one render later — without this the button would flash the
    // previous run's final elapsed value for a frame when a run is repeated.
    setElapsedMs(0);
    setState({ loading: true, result: null, error: null, durationMs: null });
    try {
      const result = await uploadBankFiles(
        bank.bankId,
        files,
        bank.importId,
        valueDate || undefined,
      );
      setState({
        loading: false,
        result,
        error: null,
        durationMs: Date.now() - startedAt,
      });
      // Newest of the value dates this run actually landed (sorted ascending by
      // the backend) — the exceptions filter jumps straight to what was just loaded.
      const landedDate = result.valueDates.length > 0
        ? result.valueDates[result.valueDates.length - 1]
        : undefined;
      onUploadSuccess(bank.bankId, landedDate);
    } catch (e: unknown) {
      showErrorToast(e, { title: t('errorToast.titleOperation') });
      setState({
        loading: false,
        result: null,
        error: null,
        durationMs: Date.now() - startedAt,
      });
    }
  }, [bank.bankId, bank.importId, files, valueDate, onUploadSuccess]);

  const uploadDisabled = files.length === 0 || !canUpload;

  return (
    <section className="bankfiles-sector" aria-label={bank.name}>
      <h2 className="bankfiles-sector__title">{bank.name}</h2>
      {bank.lastImportDate && (
        <p className="bankfiles-sector__last-date">
          {t('bankFiles.lastImportDate', { date: formatDateOnly(bank.lastImportDate) })}
        </p>
      )}

      <div className="bankfiles-sector__body">
        {/* Styled drop zone fronting the hidden native input (folder picker for folder imports) */}
        <LopFilePicker
          id={`bf-file-${bank.importId}`}
          files={files}
          onChange={handleFilesChange}
          directory={isFolder}
          disabled={!canUpload}
          title={isFolder ? t('bankFiles.chooseFolder') : t('bankFiles.chooseFile')}
          hint={t('bankFiles.noFileChosen')}
        />

        {/* As-of date override (required for Mizrahi; optional otherwise) */}
        <div className="bankfiles-sector__file-row">
          <label
            className="bankfiles-sector__file-label"
            htmlFor={`bf-date-${bank.importId}`}
          >
            {t('bankFiles.valueDateLabel')}
          </label>
          <LopDateInput
            id={`bf-date-${bank.importId}`}
            className="bankfiles-sector__date-input"
            value={valueDate}
            onChange={setValueDate}
          />
        </div>

        {/* Upload button — carries the live elapsed counter while the run is in flight */}
        <div>
          <LopButton
            id={`bf-upload-${bank.importId}`}
            variant="primary"
            icon={<Upload size={14} />}
            disabled={uploadDisabled}
            loading={state.loading}
            onClick={handleUpload}
          >
            {t('bankFiles.uploadButton')}
          </LopButton>
        </div>

        {/* Loader — an ingestion can run for tens of seconds; without this the screen
            reads as hung. aria-live so a screen reader announces it too. */}
        {state.loading && (
          <div className="bankfiles-loader" role="status" aria-live="polite">
            <span className="bankfiles-loader__bar" aria-hidden="true" />
            <span className="bankfiles-loader__text">
              {t('bankFiles.uploadingElapsed', { elapsed: formatElapsed(elapsedMs) })}
              {' - '}
              {t('bankFiles.uploadingHint')}
            </span>
          </div>
        )}

        {/* Result summary */}
        {state.result && (
          <div
            className={
              // A request can succeed while the import did nothing: every handler silently
              // skips files it doesn't recognise, so a folder of misnamed files lands zero.
              // Zero files loaded is a failed import, not a successful one — colour it as
              // such (the backend's log row already records status 'failed').
              state.result.filesLoaded === 0
                ? 'bankfiles-result bankfiles-result--error'
                : 'bankfiles-result bankfiles-result--success'
            }
            role="status"
          >
            <p className="bankfiles-result__title">
              {state.result.filesLoaded === 0
                ? t('bankFiles.uploadNothingLoaded')
                : t('bankFiles.uploadSuccess')}
            </p>
            {state.durationMs !== null && (
              <p className="bankfiles-result__line">
                {t('bankFiles.resultDuration', {
                  elapsed: formatElapsed(state.durationMs),
                })}
              </p>
            )}
            <p className="bankfiles-result__line">
              {t('bankFiles.resultFilesLoaded', {
                count: state.result.filesLoaded,
              })}
            </p>
            <p className="bankfiles-result__line">
              {t('bankFiles.resultPreInventoryRows', {
                count: state.result.preInventoryRows,
              })}
            </p>
            {state.result.valueDates.length > 0 && (
              <p className="bankfiles-result__line">
                {t('bankFiles.resultValueDates', {
                  dates: state.result.valueDates.join(', '),
                })}
              </p>
            )}
            {state.result.messages.length > 0 && (
              <ul className="bankfiles-result__messages">
                {state.result.messages.map((msg, i) => (
                  <li key={i}>{msg}</li>
                ))}
              </ul>
            )}
          </div>
        )}

        {state.error && (
          <div className="bankfiles-result bankfiles-result--error" role="alert">
            <p className="bankfiles-result__title">{state.error}</p>
          </div>
        )}
      </div>
    </section>
  );
}

// ─── Exceptions grid ──────────────────────────────────────────────────────────
const SEVERITY_LABEL: Record<number, string> = {
  1: t('bankFiles.exceptions.severity1'),
  2: t('bankFiles.exceptions.severity2'),
  3: t('bankFiles.exceptions.severity3'),
};

function useExceptionColumnDefs(): ColDef<BankImportException>[] {
  return useMemo<ColDef<BankImportException>[]>(
    () => [
      {
        field: 'valueDate',
        headerName: t('bankFiles.exceptions.valueDate'),
        width: 120,
        editable: false,
      },
      {
        field: 'exceptionCode',
        headerName: t('bankFiles.exceptions.exceptionCode'),
        width: 180,
        editable: false,
      },
      {
        field: 'severity',
        headerName: t('bankFiles.exceptions.severity'),
        width: 100,
        editable: false,
        valueFormatter: (p: ValueFormatterParams<BankImportException>) =>
          SEVERITY_LABEL[p.value as number] ?? String(p.value ?? ''),
      },
      {
        field: 'message',
        headerName: t('bankFiles.exceptions.message'),
        flex: 1,
        minWidth: 260,
        editable: false,
      },
      {
        field: 'details',
        headerName: t('bankFiles.exceptions.details'),
        flex: 1,
        minWidth: 260,
        editable: false,
      },
      {
        field: 'createdAt',
        headerName: t('bankFiles.exceptions.createdAt'),
        width: 170,
        editable: false,
        valueFormatter: (p: ValueFormatterParams<BankImportException>) =>
          formatDateTimeIL(p.value, { seconds: true }),
      },
    ],
    [],
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────
export function BankFilesPage() {
  const { user } = useAuth();
  const mayView = canView(user, 'bankFiles');
  const mayEdit = canEdit(user, 'bankFiles');

  // ── Import registry (sector cards) driven by GET /api/bank-imports ──
  const [banks, setBanks] = useState<BankDescriptor[]>([]);

  // ── Exceptions filter state (draft vs active, per HoldingsPage pattern) ──
  const [draftBankId, setDraftBankId] = useState<number>(0);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [activeBankId, setActiveBankId] = useState<number>(0);
  // Value-date filter — WITHOUT this the grid read the whole (unbounded)
  // ImportExceptions table; once the WH archiver starts moving rows older than
  // 13 months out, an unfiltered read would silently shrink. Default = the most
  // recent import's value date (per bank.lastImportDate, already loaded from the
  // registry) so the screen opens on "today's" exceptions; the user can widen it.
  const [draftValueDate, setDraftValueDate] = useState<string>('');
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [activeValueDate, setActiveValueDate] = useState<string>('');

  // Exceptions grid data
  const [rows, setRows] = useState<BankImportException[]>([]);
  const [gridLoading, setGridLoading] = useState(false);
  const [gridError, setGridError] = useState<string | null>(null);

  const getRowId = useCallback(
    (p: { data: BankImportException }) => String(p.data.exceptionId),
    [],
  );

  // Fetch exceptions for the active bank (0 = all banks) + active value date ('' = no filter).
  const fetchExceptions = useCallback((bankId: number, valueDate: string) => {
    setGridLoading(true);
    setGridError(null);
    return getBankImportExceptions(bankId || undefined, valueDate || undefined)
      .then((data) => setRows(data))
      .catch((e: unknown) => {
        showErrorToast(e, { title: t('errorToast.titleLoad') });
        setGridError(describeError(e).body);
        setRows([]);
      })
      .finally(() => setGridLoading(false));
  }, []);

  // Most recent lastImportDate across the given bank's registered imports (a bank
  // can have several — e.g. Poalim's splitters); bankId=0 (all banks) takes the max
  // across every registered import. '' when nothing has ever imported yet.
  const mostRecentDateFor = useCallback(
    (bankId: number, list: BankDescriptor[]): string => {
      const candidates = list
        .filter((b) => bankId === 0 || b.bankId === bankId)
        .map((b) => b.lastImportDate)
        .filter((d): d is string => !!d);
      if (candidates.length === 0) return '';
      return candidates.reduce((max, d) => (d > max ? d : max));
    },
    [],
  );

  // Load the import registry once, derive the sector list, and default the filter.
  useEffect(() => {
    let cancelled = false;
    getBankImports()
      .then((defs: BankImportDef[]) => {
        if (cancelled) return;
        // This screen is the daily Israeli-bank BALANCE ingestion. Foreign-bank
        // contracts (importType='contracts') have their own screen.
        const active = defs.filter((d) => d.isActive && d.importType === 'balance');
        const descriptors: BankDescriptor[] = active.map((d) => ({
          importId: d.importId,
          bankId: d.bankId,
          name: d.importName,
          sourceKind: d.sourceKind,
          lastImportDate: d.lastImportDate,
        }));
        setBanks(descriptors);
        // Default: all banks, value date = the most recent import overall.
        const defaultDate = mostRecentDateFor(0, descriptors);
        setDraftBankId(0);
        setActiveBankId(0);
        setDraftValueDate(defaultDate);
        setActiveValueDate(defaultDate);
        fetchExceptions(0, defaultDate);
      })
      .catch(() => {
        // registry load failure leaves an empty sector list; the grid stays empty
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Selecting a different bank re-defaults the (still-draft) value date to THAT
  // bank's most recent import — the user can still edit it before hitting Fetch.
  const handleBankChange = useCallback(
    (bankId: number) => {
      setDraftBankId(bankId);
      setDraftValueDate(mostRecentDateFor(bankId, banks));
    },
    [banks, mostRecentDateFor],
  );

  const handleFetch = useCallback(() => {
    setActiveBankId(draftBankId);
    setActiveValueDate(draftValueDate);
    return fetchExceptions(draftBankId, draftValueDate);
  }, [draftBankId, draftValueDate, fetchExceptions]);

  // After a successful upload, focus the exceptions on that bank + the date it just
  // landed (falls back to the bank's registry default when the run produced none).
  const handleUploadSuccess = useCallback(
    (bankId: number, landedValueDate?: string) => {
      const valueDate = landedValueDate || mostRecentDateFor(bankId, banks);
      setDraftBankId(bankId);
      setActiveBankId(bankId);
      setDraftValueDate(valueDate);
      setActiveValueDate(valueDate);
      fetchExceptions(bankId, valueDate);
    },
    [banks, mostRecentDateFor, fetchExceptions],
  );

  const columnDefs = useExceptionColumnDefs();

  // Distinct banks for the filter dropdown (an import registry may have several
  // imports per bank; the exception filter is per bank, not per import).
  const bankOptions = useMemo(() => {
    const seen = new Map<number, string>();
    for (const b of banks) if (!seen.has(b.bankId)) seen.set(b.bankId, b.name);
    return Array.from(seen, ([bankId, name]) => ({ bankId, name }));
  }, [banks]);

  if (!mayView) {
    return (
      <div className="bankfiles-access-denied" role="alert">
        {t('bankFiles.accessDenied')}
      </div>
    );
  }

  return (
    <div className="bankfiles-page">
      <h1 className="bankfiles-page__title">{t('bankFiles.pageTitle')}</h1>

      {/* ── Import sector cards (from the registry) ── */}
      <div className="bankfiles-sectors">
        {banks.map((bank) => (
          <BankSector
            key={bank.importId}
            bank={bank}
            canUpload={mayEdit}
            onUploadSuccess={handleUploadSuccess}
          />
        ))}
      </div>

      {/* ── Import exceptions section ── */}
      <div className="bankfiles-grid-section">
        <h2 className="bankfiles-grid-section__title">
          {t('bankFiles.exceptions.title')}
        </h2>

        {/* Filter controls */}
        <div className="bankfiles-filter-bar" dir="rtl">
          <label className="bankfiles-filter-bar__label" htmlFor="bf-bankid">
            {t('bankFiles.filterBankLabel')}
          </label>
          <select
            id="bf-bankid"
            className="bankfiles-filter-bar__select"
            value={draftBankId}
            onChange={(e) => handleBankChange(Number(e.target.value))}
          >
            <option value={0}>{t('bankFiles.exceptions.allBanks')}</option>
            {bankOptions.map((b) => (
              <option key={b.bankId} value={b.bankId}>
                {b.name}
              </option>
            ))}
          </select>

          <label className="bankfiles-filter-bar__label" htmlFor="bf-exceptions-date">
            {t('bankFiles.filterDateLabel')}
          </label>
          <LopDateInput
            id="bf-exceptions-date"
            className="bankfiles-filter-bar__select"
            value={draftValueDate}
            onChange={setDraftValueDate}
          />

          <LopButton variant="primary" loading={gridLoading} onClick={handleFetch}>
            {t('bankFiles.filterFetchButton')}
          </LopButton>
        </div>

        {gridError && (
          <div className="bankfiles-grid-error" role="alert">
            {gridError}
          </div>
        )}

        {!gridLoading && !gridError && rows.length === 0 && (
          <div className="bankfiles-exceptions-empty" role="status">
            {t('bankFiles.exceptions.none')}
          </div>
        )}

        <div className="bankfiles-grid-container">
          <LopAgGrid<BankImportException>
            rowData={rows}
            columnDefs={columnDefs}
            getRowId={getRowId}
            loading={gridLoading}
          />
        </div>
      </div>
    </div>
  );
}
